export type FormatType = 'avif' | 'webp' | 'jpeg';

export type ImageOptimizeOptions = {
    quality?: number;
    height?: number;
    width?: number;
    fit?: 'scale-down' | 'contain' | 'cover' | 'crop' | 'pad';
    /**
     * Other posible parameters:
     * https://developers.cloudflare.com/images/transform-images/transform-via-url/
     */
    // anim?: boolean;
    // background?: string;
    // blur?: number;
    // border?: todo;
    // brightness?: number;
    // compression?: 'fast';
    // contrast?: number;
    // dpr?: number;
    // format?: 'auto' | 'avif' | 'webp' | 'jpeg' | 'baseline-jpeg' | 'json';
    // gamma?: number;
    // gravity: todo;
    // metadata?: 'keep' | 'copyright' | 'none';
    // onerror?: 'redirect';
    // rotate?: number;
    // sharpen?: number;
    // trim?: string;
};

export type CloudflareImageOptimizationParams = ImageOptimizeOptions & {
    imgSrc: string;
    format: FormatType;
};

export const imageOptimizedUrl = (
    params: CloudflareImageOptimizationParams,
    baseWebsiteUrl: string | null,
    convertGcsMedia: boolean,
): string | null => {
    const { imgSrc, ...rest } = params;
    const paramChunks: Array<string> = [];

    if (imgSrc.trim().length === 0) {
        return null;
    }

    for (const [key, value] of Object.entries(rest)) {
        paramChunks.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }

    const urlParameters = paramChunks.join(',');
    const websiteUrl = baseWebsiteUrl === null ? '' : baseWebsiteUrl; // prod = relative; stage & local = fixed path

    const imgUrlToOptimize = convertGcsMedia ? convertGcsMediaToPublicUrl(imgSrc) : imgSrc;

    return `${websiteUrl}/cdn-cgi/image/${urlParameters}/${imgUrlToOptimize}`;
};

/**
 * Converts a Google Cloud Storage API media link to a direct public URL.
 * This conversion is necessary because Cloudflare Image Optimization 
 * fails to process GCS API media links (e.g., `.../download/storage/v1/b/...`) 
 * in certain environments. Using direct public URLs resolves this issue.
 *
 * @param url - The original image source URL
 * @returns The converted public GCS URL, or the original URL if no conversion was needed
 */
const convertGcsMediaToPublicUrl = (url: string): string => {
    if (!isGoogleMediaType(url)) {
        return url;
    }

    try {
        const parsedUrl = new URL(url);
        const pathSegments = parsedUrl.pathname.split('/');

        const bucketIndex = pathSegments.indexOf('b');
        const objectIndex = pathSegments.indexOf('o');

        if (bucketIndex === -1 || objectIndex === -1) {
            return url;
        }

        const bucketName = pathSegments[bucketIndex + 1];
        const objectName = pathSegments[objectIndex + 1];

        if (!bucketName || !objectName) {
            console.warn("Failed to parse Google media url - corrupted: ", url);
            return url;
        }

        return `https://storage.googleapis.com/${decodeURIComponent(bucketName)}/${decodeURIComponent(objectName)}`;

    } catch(err) {
        console.warn("Failed to parse Google media url: ", url, err);
    }

    return url;
};

const isGoogleMediaType = (url: string): boolean => {
    return url.includes('googleapis.com/download/storage/v1/b/');
};
